import axios from 'axios';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { GoogleMapsBtn } from '@/components/GoogleMapsBtn';
import { EmployeeAvatar } from '@/components/EmployeeAvatar';
import {
  HardHat, MapPin, Users, Package, Wrench, ChevronRight,
  LogIn, LogOut, Clock, CheckCircle, CalendarDays, Home, TreePine,
  Phone, ClipboardList
} from 'lucide-react';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

/**
 * Tableau de bord terrain — affiché dans Mon Espace pour chef_equipe et peintre.
 * Montré en haut de page, avant le reste.
 */
export function TableauBordTerrain({ token, role }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employees/terrain-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 200) setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <Card className="border-2 border-blue-100 bg-gradient-to-br from-blue-50 to-slate-50">
        <CardContent className="py-6 flex items-center gap-3">
          <div className="animate-spin w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full" />
          <span className="text-slate-500 text-sm">Chargement du chantier...</span>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { bons_aujourd_hui, bons_prochains, today } = data;
  const hasToday = bons_aujourd_hui.length > 0;
  const hasProchains = bons_prochains.length > 0;

  // Aucun chantier ni aujourd'hui ni à venir
  if (!hasToday && !hasProchains) {
    return (
      <Card className="border-2 border-slate-200 bg-slate-50">
        <CardContent className="py-5 flex items-center gap-3">
          <CalendarDays className="w-8 h-8 text-slate-300 shrink-0" />
          <div>
            <p className="font-semibold text-slate-600">Pas de chantier aujourd'hui</p>
            <p className="text-sm text-slate-400">Aucun bon de travail prévu pour aujourd'hui ni dans les prochains jours.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Section 1 : Mon chantier d'aujourd'hui (vert) — affichée seulement s'il y a du travail aujourd'hui */}
      {hasToday && (
        <BlocChantiers
          titre="Mon chantier d'aujourd'hui"
          sousTitre={`${bons_aujourd_hui.length} bon${bons_aujourd_hui.length > 1 ? 's' : ''} de travail en cours`}
          bons={bons_aujourd_hui}
          isToday
          today={today}
          role={role}
          couleurIcone="text-green-600"
          couleurBg="bg-green-50"
          couleurPastille="bg-green-500 animate-pulse"
          defaultOpen
        />
      )}

      {/* Section 2 : Mes prochains chantiers (ambre) — toujours affichée s'il y a du travail à venir */}
      {hasProchains && (
        <BlocChantiers
          titre="Mes prochains chantiers"
          sousTitre={`${bons_prochains.length} bon${bons_prochains.length > 1 ? 's' : ''} à venir`}
          bons={bons_prochains}
          isToday={false}
          today={today}
          role={role}
          couleurIcone="text-amber-600"
          couleurBg="bg-amber-50"
          couleurPastille="bg-amber-400"
          defaultOpen={!hasToday}
        />
      )}
    </div>
  );
}

// Bloc pliable générique qui regroupe plusieurs BonCard sous un header cliquable
export function BlocChantiers({ titre, sousTitre, bons, isToday, today, role, couleurIcone, couleurBg, couleurPastille, defaultOpen = false }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(defaultOpen);

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
      <div
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setExpanded(v => !v)}
        data-testid={`bloc-chantiers-${isToday ? 'aujourdhui' : 'prochains'}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${couleurBg}`}>
            <HardHat className={`w-5 h-5 ${couleurIcone}`} />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
              {titre}
              <span className={`w-2 h-2 rounded-full ${couleurPastille}`} />
            </h3>
            <span className="text-xs text-slate-500">{sousTitre}</span>
          </div>
        </div>
        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform shrink-0 ml-2 ${expanded ? 'rotate-90' : ''}`} />
      </div>
      {expanded && (
        <div className="p-4 pt-0 border-t border-slate-100 space-y-3">
          {bons.map((bon, idx) => (
            <BonCard key={bon.id || idx} bon={bon} role={role} isToday={isToday} today={today} navigate={navigate} />
          ))}
        </div>
      )}
    </div>
  );
}

function maskAdresseLavage(adresse, ville) {
  const a = (adresse || '').trim();
  const v = (ville || '').trim();
  if (!a && !v) return '';
  const masked = a ? `${a.slice(0, 3)}${a.length > 3 ? '…' : ''}` : '';
  return [masked, v].filter(Boolean).join(', ');
}

function BonCard({ bon, role, isToday, today, navigate }) {
  const isChef = role === 'chef_equipe'; // Cat H — dispatch UI : le chef d'équipe voit les actions "démarrer étape" / "assigner", les peintres non. Rôle terrain, pas RBAC.  // E3b-whitelist : remplacé ci-dessous par aLeRole si user dispo
  const isLavage = !!bon.est_route_lavage;
  const adresseComplete = isLavage
    ? maskAdresseLavage(bon.adresse_travaux, bon.ville_travaux)
    : [bon.adresse_travaux, bon.ville_travaux].filter(Boolean).join(', ');
  const isExt = bon.type_travaux === 'exterieur';
  const TypeIcon = isExt ? TreePine : Home;

  // Prochaine date si pas aujourd'hui
  const nextDate = !isToday && bon.dates_chantier?.length > 0
    ? new Date(bon.dates_chantier[0] + 'T12:00:00').toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' })
    : null;

  const etapesTotal = bon.etapes?.length || 0;
  const etapesDone = bon.etapes?.filter(e => e.completee).length || 0;

  return (
    <Card className="border-2 border-blue-200 bg-gradient-to-br from-white to-blue-50 overflow-hidden">
      {/* Bandeau statut */}
      <div className={`px-4 py-2 flex items-center justify-between ${isToday ? 'bg-blue-600' : 'bg-amber-500'}`}>
        <div className="flex items-center gap-2">
          <HardHat className="w-4 h-4 text-white" />
          <span className="text-white font-semibold text-sm">
            {isToday ? 'Chantier aujourd\'hui' : `Prochain chantier — ${nextDate}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <TypeIcon className="w-4 h-4 text-white opacity-75" />
          <span className="text-white text-xs opacity-75 capitalize">{bon.type_travaux || 'peinture'}</span>
        </div>
      </div>

      <CardContent className="p-4 space-y-4">
        {/* Client & Adresse */}
        <div>
          <p className="text-lg font-bold text-slate-800">{bon.client_nom || 'Client'}</p>
          {bon.telephone_client && (
            <a href={`tel:${bon.telephone_client}`} className="flex items-center gap-1 text-blue-600 text-sm mt-0.5">
              <Phone className="w-3.5 h-3.5" /> {bon.telephone_client}
            </a>
          )}
          {adresseComplete && (
            <div className="flex items-center gap-2 mt-2">
              <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
              <span className="text-slate-600 text-sm">{adresseComplete}</span>
              {!isLavage && (
                <GoogleMapsBtn address={bon.adresse_travaux} ville={bon.ville_travaux} codePostal={bon.code_postal_travaux} />
              )}
            </div>
          )}
        </div>

        {/* Description des travaux */}
        {bon.description_travaux && (
          <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Instructions</p>
            <div className="text-sm text-slate-700 leading-relaxed line-clamp-4"
              dangerouslySetInnerHTML={{ __html: (() => {
                const d = bon.description_travaux || '';
                return /<[a-z][\s\S]*>/i.test(d) ? d : d.replace(/\r?\n/g, '<br>');
              })() }}
            />
          </div>
        )}

        {/* Étapes (progress) */}
        {etapesTotal > 0 && (
          <div>
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
              <span>Avancement</span>
              <span className="font-semibold">{etapesDone}/{etapesTotal}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all"
                style={{ width: `${etapesTotal > 0 ? (etapesDone / etapesTotal) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {/* Équipe avec punch status (Chef seulement) */}
        {isChef && bon.team_punch?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1">
              <Users className="w-3.5 h-3.5" /> Mon équipe
            </p>
            <div className="grid grid-cols-2 gap-2">
              {bon.team_punch.map((member, i) => (
                <div key={member.employee_id || i} className={`flex items-center gap-2 p-2 rounded-lg border ${member.punched_in ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                  <EmployeeAvatar photoUrl={member.photo_url} prenom={member.prenom || '?'} nom={member.nom || ''} size="sm" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-700 truncate">{member.prenom} {member.nom}</p>
                    {member.punched_in
                      ? <p className="text-xs text-green-600 flex items-center gap-0.5"><LogIn className="w-2.5 h-2.5" /> {member.heure_debut?.slice(0,5)}</p>
                      : <p className="text-xs text-slate-400 flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" /> Pas encore</p>
                    }
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Produits à commander (Chef seulement) */}
        {isChef && bon.produits?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1">
              <Package className="w-3.5 h-3.5" /> Produits prévus ({bon.produits.length})
            </p>
            <div className="space-y-1">
              {bon.produits.slice(0, 3).map((prod, i) => (
                <div key={prod.id || prod._id || i} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 truncate">{prod.nom || prod.produit_nom}</span>
                  <span className="text-slate-400 text-xs shrink-0 ml-2">
                    {prod.quantite_commandee || prod.quantite || '?'} {prod.unite || 'un'}
                  </span>
                </div>
              ))}
              {bon.produits.length > 3 && (
                <p className="text-xs text-slate-400">+{bon.produits.length - 3} autres produits</p>
              )}
            </div>
          </div>
        )}

        {/* Équipements de location (Chef seulement) */}
        {isChef && bon.equipements?.filter(e => !e.utilisation_terminee).length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1">
              <Wrench className="w-3.5 h-3.5" /> Équipements actifs
            </p>
            <div className="space-y-1">
              {bon.equipements.filter(e => !e.utilisation_terminee).map((eq, i) => (
                <div key={eq.id || eq._id || i} className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500 shrink-0" />
                  <span className="text-slate-600 truncate">{eq.nom_equipement || eq.equipement_nom || 'Équipement'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA - Ouvrir le bon ou la route de lavage */}
        {bon.est_route_lavage ? (
          <>
            {/* Mini-aperçu du 1er arrêt pour préparation mentale (adresse masquée) */}
            {bon.premier_client_lavage && (
              <div
                className="rounded-lg border border-cyan-200 bg-cyan-50/70 p-3"
                data-testid="route-lavage-preview"
              >
                <p className="text-[11px] font-semibold text-cyan-700 uppercase tracking-wide mb-1">
                  Prochain arrêt
                </p>
                <p className="text-sm font-bold text-slate-800 truncate" data-testid="route-lavage-preview-client">
                  1. {bon.premier_client_lavage}
                </p>
                {(bon.premier_client_adresse_lavage || bon.premier_client_ville_lavage) && (
                  <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1" data-testid="route-lavage-preview-adresse">
                    <MapPin className="w-3 h-3" />
                    {maskAdresseLavage(bon.premier_client_adresse_lavage, bon.premier_client_ville_lavage) || 'Adresse dévoilée au démarrage'}
                  </p>
                )}
                {bon.heure_debut_route_lavage ? (
                  <p className="text-xs text-cyan-700 mt-1 flex items-center gap-1" data-testid="route-lavage-preview-heure">
                    <Clock className="w-3 h-3" />
                    Route démarrée à {new Date(bon.heure_debut_route_lavage).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                ) : (
                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1" data-testid="route-lavage-preview-heure">
                    <Clock className="w-3 h-3" />
                    Heure d'arrivée calculée au démarrage
                  </p>
                )}
              </div>
            )}
            <Button
              onClick={() => navigate('/routes-lavage', { state: { selectedRouteId: bon.id } })}
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="btn-ouvrir-route-terrain"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              Ouvrir la route de lavage ({bon.nb_clients_lavage || 0} clients)
              <ChevronRight className="w-4 h-4 ml-auto" />
            </Button>
          </>
        ) : (
          <Button
            onClick={() => navigate(`/crm/projets/${bon.dossier_id}?tab=bon-travail&bonId=${bon.id}`)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="btn-ouvrir-bon-terrain"
          >
            <ClipboardList className="w-4 h-4 mr-2" />
            Ouvrir le bon de travail
            <ChevronRight className="w-4 h-4 ml-auto" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
